import React from 'react'
import {
  AddPhotoAlternateOutlined, AddRounded, ArrowUpwardRounded, ArrowDownwardRounded, ChevronLeftRounded, ChevronRightRounded, DeleteOutlineRounded,
} from '@mui/icons-material'
import { api } from '../../function/axios'
import { ParagraphEditor } from '../../container/marketing/campaign/ParagraphEditor'
import { paragraphText } from '../../container/marketing/campaign/paragraphFormat'
import { Paragraph } from '../../container/marketing/campaign/CampaignPreviewV2'
import s from './SlidesV2.module.css'

/*
 * An update's carousel: slides of a picture, a title and a text, which the
 * user leafs through like an Instagram carousel. A picture is uploaded when
 * it is chosen (POST /admin-messages/images → its S3 key); the preview here
 * shows the local file, the dashboard reads it back by the key.
 */

export type Slide = {
  id: string
  image?: string
  /** the chosen file, for the preview — not sent */
  preview?: string
  uploading?: boolean
  error?: string
  title: string
  text: string
}

export const SLIDES_MAX = 10
export const SLIDE_TITLE_MAX = 80
export const SLIDE_TEXT_MAX = 500
const IMAGE_MAX = 2 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

let seq = 0
export const newSlide = (): Slide => ({ id: `s${Date.now()}-${seq++}`, title: '', text: '' })
export const slideIsEmpty = (x: Slide) => !x.image && !x.title.trim() && !paragraphText(x.text).trim()

/** what goes to the backend */
export const slidesBody = (slides: Slide[]) =>
  slides.filter((x) => !slideIsEmpty(x)).map((x) => ({ image: x.image || undefined, title: x.title.trim() || undefined, text: x.text.trim() || undefined }))

export function slidesProblem(slides: Slide[]): string {
  if (slides.some((x) => x.uploading)) return 'A picture is still uploading'
  if (slides.some((x) => x.error)) return 'A slide’s picture was not uploaded — choose it again or remove it'
  if (slides.some((x) => paragraphText(x.text).trim().length > SLIDE_TEXT_MAX)) return `A slide’s text is longer than ${SLIDE_TEXT_MAX} characters`
  return ''
}

export function SlidesEditor({ slides, onChange }: { slides: Slide[]; onChange: (next: Slide[] | ((prev: Slide[]) => Slide[])) => void }) {
  const patch = (id: string, p: Partial<Slide>) => onChange((prev) => prev.map((x) => (x.id === id ? { ...x, ...p } : x)))
  const move = (i: number, d: number) => onChange((prev) => {
    const next = [...prev]
    const [x] = next.splice(i, 1)
    next.splice(i + d, 0, x)
    return next
  })

  async function pick(id: string, file?: File | null) {
    if (!file) return
    if (!IMAGE_TYPES.includes(file.type)) { patch(id, { error: 'JPG, PNG, WebP or GIF only' }); return }
    if (file.size > IMAGE_MAX) { patch(id, { error: 'The picture is larger than 2 MB' }); return }
    patch(id, { preview: URL.createObjectURL(file), image: undefined, uploading: true, error: undefined })
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api({ method: 'POST', url: '/admin-messages/images', body: form })
      patch(id, { image: data?.key, uploading: false, error: data?.key ? undefined : 'Not uploaded' })
    } catch (e: any) {
      const m = e?.response?.data?.message
      patch(id, { uploading: false, error: (Array.isArray(m) ? m.join('; ') : m) || 'Not uploaded' })
    }
  }

  return (
    <div className={s.list}>
      {slides.map((x, i) => (
        <div key={x.id} className={s.slide}>
          <label className={`${s.pic} ${x.error ? s.picErr : ''}`} title="Choose a picture — JPG, PNG, WebP or GIF, up to 2 MB">
            {x.preview ? <img src={x.preview} alt="" /> : <span className={s.picEmpty}><AddPhotoAlternateOutlined sx={{ fontSize: 22 }} />Picture</span>}
            {x.uploading && <span className={s.picState}>Uploading…</span>}
            <input type="file" accept={IMAGE_TYPES.join(',')} onChange={(e) => { void pick(x.id, e.target.files?.[0]); e.target.value = '' }} />
          </label>
          <div className={s.fields}>
            <div className={s.slideHead}>
              <b>Slide {i + 1}</b>
              {x.error && <span className={s.err}>{x.error}</span>}
              <span className={s.grow} />
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up"><ArrowUpwardRounded sx={{ fontSize: 16 }} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === slides.length - 1} aria-label="Move down" title="Move down"><ArrowDownwardRounded sx={{ fontSize: 16 }} /></button>
              <button type="button" onClick={() => onChange((prev) => prev.filter((y) => y.id !== x.id))} aria-label="Remove the slide" title="Remove the slide"><DeleteOutlineRounded sx={{ fontSize: 16 }} /></button>
            </div>
            <input className={s.input} value={x.title} maxLength={SLIDE_TITLE_MAX} onChange={(e) => patch(x.id, { title: e.target.value })} placeholder="Slide title" />
            <ParagraphEditor value={x.text} onChange={(v) => patch(x.id, { text: v })} placeholder="A few words about it" />
          </div>
        </div>
      ))}
      {slides.length < SLIDES_MAX && (
        <button type="button" className={s.add} onClick={() => onChange((prev) => [...prev, newSlide()])}>
          <AddRounded sx={{ fontSize: 17 }} />Add a slide <em>{slides.length} / {SLIDES_MAX}</em>
        </button>
      )}
    </div>
  )
}

/** the preview — as the dashboard shows it */
export function SlidesCarousel({ slides, actionName }: { slides: Slide[]; actionName?: string }) {
  const shown = slides.filter((x) => !slideIsEmpty(x))
  const [i, setI] = React.useState(0)
  const at = Math.min(i, Math.max(0, shown.length - 1))
  if (!shown.length) return <div className={s.carEmpty}>Add a slide — a picture, a title, a few words</div>
  const x = shown[at]
  return (
    <div className={s.car}>
      <div className={s.carPic}>
        {x.preview ? <img src={x.preview} alt="" /> : <span className={s.carNoPic}>No picture</span>}
        {shown.length > 1 && <span className={s.carCount}>{at + 1} / {shown.length}</span>}
        {at > 0 && <button type="button" className={`${s.carArrow} ${s.carPrev}`} onClick={() => setI(at - 1)} aria-label="Previous"><ChevronLeftRounded /></button>}
        {at < shown.length - 1 && <button type="button" className={`${s.carArrow} ${s.carNext}`} onClick={() => setI(at + 1)} aria-label="Next"><ChevronRightRounded /></button>}
      </div>
      {shown.length > 1 && (
        <div className={s.carDots}>{shown.map((y, k) => <button key={y.id} type="button" className={k === at ? s.carDotOn : ''} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} />)}</div>
      )}
      <div className={s.carText}>
        {x.title.trim() && <b>{x.title.trim()}</b>}
        {paragraphText(x.text).trim() && <Paragraph value={x.text} phone={false} />}
        {at === shown.length - 1 && actionName?.trim() && <span className={s.carBtn}>{actionName.trim()}</span>}
      </div>
    </div>
  )
}
